import {HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse} from "@angular/common/http";
import {Injectable} from "@angular/core";

import {ApiClient} from "framework/services/api.client";
import {DedupeInterceptorStore, matchesAnyPrefix} from "framework/services/dedupe-interceptor/dedupe-interceptor-store";

import {finalize, Observable, of, shareReplay} from "rxjs";
import {tap} from "rxjs/operators";

interface InFlightRequest
{
    url: string;
    observable: Observable<HttpEvent<any>>;
}

@Injectable({providedIn: "root"})
export class DedupeInterceptor implements HttpInterceptor
{
    readonly debugging = false;

    private m_inFlightRequests = new Map<string, InFlightRequest>();

    constructor(readonly cache: DedupeInterceptorStore)
    {
        if (this.debugging) console.log("Dedupe interceptor logging on");
    }

    private invalidate(prefixes: string[],
                       method?: string,
                       url?: string)
    {
        if (this.debugging && (method || url)) console.log(`cleared dedupe interceptor scopes [${prefixes}] from ${method} ${url}`);

        for (const [hash, inFlight] of this.m_inFlightRequests)
        {
            if (matchesAnyPrefix(inFlight.url, prefixes)) this.m_inFlightRequests.delete(hash);
        }

        this.cache.invalidate(prefixes);
    }

    public intercept(req: HttpRequest<any>,
                     next: HttpHandler): Observable<HttpEvent<any>>
    {
        const invalidateScopes = this.cache.invalidateScopes(req.method, req.url) ?? [];
        if (invalidateScopes.length) this.invalidate(invalidateScopes, req.method, req.url);

        const ttl = this.cache.dedupeTTL(req.method, req.url);
        if (ttl < 0) return next.handle(req);

        const reqHash = this.canonicalizedHash(req);

        const cachedResponse = this.cache.get(reqHash);
        if (cachedResponse) return of(cachedResponse);

        const inFlightRequest = this.m_inFlightRequests.get(reqHash);
        if (inFlightRequest) return inFlightRequest.observable;

        const request$ = next.handle(req)
                             .pipe(
                                 tap(event =>
                                     {
                                         if (ttl > 0 && event instanceof HttpResponse && event.status >= 200 && event.status < 300)
                                         {
                                             this.cache.set(reqHash, req.url, event, ttl);
                                         }
                                     }),
                                 finalize(() => { this.m_inFlightRequests.delete(reqHash); }),
                                 shareReplay({
                                                 bufferSize: 1,
                                                 refCount  : true
                                             })
                             );

        this.m_inFlightRequests.set(reqHash, {
                                        url       : req.url,
                                        observable: request$
                                    });

        return request$;
    }

    private canonicalizedHash(request: HttpRequest<any>): string
    {
        const canonicalized = this.canonicalizeRequest(request);
        const json          = JSON.stringify(canonicalized);
        return "" + this.fnv1aHash(json);
    }

    private canonicalizeRequest(request: HttpRequest<any>): object
    {
        const canonicalized = this.canonicalizeObject(request);
        if (Array.isArray(canonicalized.headers?.lazyUpdate))
        {
            const lazyHeaders: any[] = canonicalized.headers.lazyUpdate;
            const requestIdIdx       = lazyHeaders.findIndex(obj => obj.name == ApiClient.IdentifierHeaderName);
            if (requestIdIdx >= 0) lazyHeaders.splice(requestIdIdx, 1);
        }
        return canonicalized;
    }

    private canonicalizeObject(obj: any): any
    {
        if (Array.isArray(obj)) return obj.map(v => this.canonicalizeObject(v));

        if (obj !== null && typeof obj === "object")
        {
            const canonicalized: any = {};
            for (const key of Object.keys(obj)
                                    .sort())
            {
                canonicalized[key] = this.canonicalizeObject(obj[key]);
            }
            return canonicalized;
        }

        return obj;
    }

    private fnv1aHash(str: string): number
    {
        let hash = 2166136261;
        for (let i = 0; i < str.length; i++)
        {
            hash ^= str.charCodeAt(i);
            hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
        }
        return hash >>> 0;
    }
}
